import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useModelsQuery } from 'src/api/models';
import { useAppContext } from 'src/contexts/AppContext';
import { useFilterContext } from 'src/contexts/FilterContext';

/**
 * Warning banner shown under the page subtitle while the user is viewing a
 * Quick preview model whose Full counterpart is still being built (the quick
 * run finishes in minutes and is published first; the full run takes hours).
 *
 * Pairing: the selected model version's modelParametersId identifies the
 * Quick model in the models list; its Full partner shares modelName +
 * createdAt — the same pair key the backend and the Settings tab use.
 */
export const QuickModelNotice: React.FC = () => {
  const { currentCustomer } = useAppContext();
  const { currentModelVersion } = useFilterContext();

  const isQuickSelected = currentModelVersion?.quickRun === true;
  // An empty customer disables the query, so the models list is only
  // fetched while a quick model is actually selected.
  const customerId = isQuickSelected
    ? (currentCustomer?.customerName ?? '')
    : '';
  const { data: models = [] } = useModelsQuery(customerId);

  const fullModelInProgress = useMemo(() => {
    const quickModel = models.find(
      (m) => m.id === currentModelVersion?.modelParametersId,
    );
    if (!quickModel) return false;

    const fullModel = models.find(
      (m) =>
        !m.quickRun &&
        m.modelName === quickModel.modelName &&
        m.createdAt === quickModel.createdAt,
    );
    return (
      fullModel?.status === 'running' || fullModel?.status === 'not_started'
    );
  }, [models, currentModelVersion?.modelParametersId]);

  if (!isQuickSelected || !fullModelInProgress) return null;

  return (
    // The theme's Alert override paints the default `subtle` variant in
    // error colors, so the palette is set explicitly here: the blue
    // "running" status tokens — this is an in-progress notice, not an error.
    <Alert
      status="info"
      bg="statusBg.running"
      color="content.title"
      borderColor="status.text.running"
      borderRadius="12px"
      alignItems="flex-start"
      fontFamily="marketingPage">
      <AlertIcon color="status.text.running" />
      <AlertDescription fontSize="sm">
        You are viewing the quick preview model — the full model is still being
        built. Treat these numbers as early estimates and avoid basing final
        decisions on them; results will be refined once the full model is ready.
      </AlertDescription>
    </Alert>
  );
};
